import type {
  InteractionEnvelope,
  InteractionEnvelopes,
} from '@/common/types/interaction';
import { BuyerAgreementStatus } from '@/views/CreateOffer/hooks/useBuyerAgreementStatus';
import type { CoreRequest } from '$api-clients/hooks';

const OFFER_ELIGIBLE_ENVELOPE_STATUSES: InteractionEnvelope['status'][] = [
  'sent',
  'partiallySigned',
  'notFinalized',
  'completed',
  'delivered',
];

const ATTACHMENT_LABEL_MAP: Record<string, string> = {
  coverLetter: 'Cover Letter',
  proofOfFunds: 'Proof of Funds',
  disclosures: 'Signed Disclosures',
  preQual: 'Pre Qual',
};

export const getReasonOfferGenerationIsDisabled = ({
  attachments,
  buyerAgreementStatus,
  buyerExists,
  envelopes,
  isUploadingAttachment,
}: {
  attachments?: CoreRequest<'/interactions/{interactionId}/attachments'>['Data'];
  buyerAgreementStatus: BuyerAgreementStatus;
  buyerExists: boolean;
  envelopes?: InteractionEnvelopes;
  isUploadingAttachment: boolean;
}): string | null | undefined => {
  // No failed uploaded documents can exist
  const failedUploadedDocument = attachments?.find(
    (attachment) => attachment.file?.uploadStatus === 'failed',
  );
  if (failedUploadedDocument) {
    const label = ATTACHMENT_LABEL_MAP[failedUploadedDocument.type];
    return label
      ? `Remove failed uploaded ${label} attachment`
      : 'Remove failed uploaded attachment';
  }

  if (isUploadingAttachment) {
    return 'Please wait for your attachment to upload';
  }

  if (buyerAgreementStatus === BuyerAgreementStatus.EXPIRED) {
    return 'Buyer Agreement must not be expired to generate Offer';
  }

  if (buyerExists && buyerAgreementStatus !== BuyerAgreementStatus.SIGNED) {
    return 'Buyer Agreement must be signed to generate Offer';
  }

  if (!envelopes) {
    return undefined;
  }

  const validEnvelopeExists = envelopes.some(({ status }) =>
    OFFER_ELIGIBLE_ENVELOPE_STATUSES.includes(status),
  );

  return validEnvelopeExists ? null : 'Create a valid envelope';
};
